import { Directive, ElementRef, HostBinding, Input, OnChanges, OnDestroy, Renderer2 } from '@angular/core';

import { OddsConfig } from '@frontend/sports/common/client-config-data-access';
import { SimpleCallback } from '@frontend/sports/common/core/utils/extended-types';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { TimerService } from '@frontend/sports/common/core/utils/timer';
import { NativePrice, NativePriceFormatComparerService } from '@frontend/sports/odds/feature/native-price';

@Directive({
    selector: '[msOptionIndicator]',
    standalone: true,
})
export class OptionIndicatorDirective implements OnChanges, OnDestroy {
    @Input('msOptionIndicator') price?: NativePrice;
    @Input('msOptionIndicatorPriceId') priceId?: number; // @Ventsi we need to have a reset value, because there is no indication if prev and current odds are coming from same price.
    @HostBinding('class') className = 'option-indicator';

    private readonly INCREASED_CLASS = 'increased';
    private readonly DECREASED_CLASS = 'decreased';

    private removeOddsAnimationCallback?: SimpleCallback = undefined;

    private clearInterval?: SimpleCallback;

    constructor(
        private renderer: Renderer2,
        private elementRef: ElementRef,
        private timerService: TimerService,
        private oddsFormatComparerService: NativePriceFormatComparerService,
        private oddsConfig: OddsConfig,
    ) {}

    ngOnChanges(changes: ISimpleChanges<OptionIndicatorDirective>): void {
        const odd = changes.price;

        this.removeClass(this.INCREASED_CLASS);
        this.removeClass(this.DECREASED_CLASS);

        this.clearInterval?.();

        if (!odd || !odd.currentValue || !odd.previousValue) {
            // when option becomes or was offline, then do nothing.
            return;
        }

        const priceId = changes.priceId;

        if (priceId && priceId.currentValue !== priceId.previousValue) {
            // When price id for this option indicator is changed, then do nothing.
            return;
        }

        if (odd && !odd.firstChange) {
            if (this.oddsFormatComparerService.greaterThan(odd.currentValue, odd.previousValue)) {
                this.handleOddsChange(this.INCREASED_CLASS);
            }

            if (this.oddsFormatComparerService.lessThan(odd.currentValue, odd.previousValue)) {
                this.handleOddsChange(this.DECREASED_CLASS);
            }
        }
    }

    private handleOddsChange(className: string): void {
        this.removeOddsAnimationCallback?.();
        this.scheduleRemoveClass(className);
        this.addClass(className);
    }

    ngOnDestroy(): void {
        this.clearInterval?.();
    }

    private addClass(className: string): void {
        this.clearInterval = this.timerService.setTimeout(() => this.renderer.addClass(this.elementRef.nativeElement, className));
    }

    private removeClass(className: string): void {
        this.renderer.removeClass(this.elementRef.nativeElement, className);
    }

    private scheduleRemoveClass(className: string): void {
        this.removeOddsAnimationCallback = this.timerService.setTimeout(
            () => this.removeClass(className),
            this.oddsConfig.display.oddsIndicatorAnimationDuration,
        );
    }
}
